import { useEffect } from "react";
import { useMap, Popup } from "react-leaflet";

// Helper functions for generating feature info HTML
function generateFeatureInfoHTML(
  features,
  generateViewerUrl,
  getDrawingIdInfo
) {
  if (features.length === 0) {
    return `<div style="
      padding: 48px 32px;
      text-align: center;
      color: #64748b;
      font-weight: 500;
      font-size: 14px;
    ">
      <div style="
        width: 64px;
        height: 64px;
        margin: 0 auto 16px;
        background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
        border-radius: 16px;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 32px;
      ">📍</div>
      <div style="font-size: 15px; color: #475569; margin-top: 8px;">No features found at this location</div>
    </div>`;
  }

  // Professional modern design
  let html = `<div style="
    max-height: 70vh;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 0;
    background: #ffffff;
  ">
    <!-- Header Section -->
    <div style="
      background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
      color: white;
      padding: 20px 24px;
      position: relative;
      overflow: hidden;
    ">
      <div style="
        position: absolute;
        top: -50%;
        right: -50%;
        width: 200%;
        height: 200%;
        background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
        pointer-events: none;
      "></div>
      <div style="position: relative; z-index: 1;">
        <div style="
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 4px;
        ">
          <div style="
            width: 40px;
            height: 40px;
            background: rgba(255, 255, 255, 0.15);
            backdrop-filter: blur(10px);
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 20px;
          ">📍</div>
          <div>
            <div style="
              font-size: 18px;
              font-weight: 700;
              letter-spacing: -0.02em;
              margin-bottom: 2px;
            ">Feature Information</div>
            <div style="
              font-size: 12px;
              color: rgba(255, 255, 255, 0.7);
              font-weight: 500;
            ">${features.length} feature${
    features.length > 1 ? "s" : ""
  } found</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Features Container -->
    <div style="padding: 20px;">`;

  features.forEach((f, index) => {
    const props = f.properties || {};
    // Check if this is a custom AsBuilt file
    const isCustomAsBuilt = props._isCustomAsBuilt === true;
    const layerName = isCustomAsBuilt
      ? "Custom AsBuilt File"
      : f.id
      ? f.id.split(".")[0]
      : `Feature ${index + 1}`;

    // Try to generate drawing link
    const viewerUrl = generateViewerUrl(layerName, props);
    const drawingInfo = getDrawingIdInfo(props);

    // Determine color scheme based on layer type
    let colorScheme = {
      primary: "#6366f1",
      light: "#eef2ff",
      dark: "#4f46e5",
      badge: "#818cf8",
    };

    if (isCustomAsBuilt) {
      // Special color scheme for custom AsBuilt files (purple/magenta)
      colorScheme = {
        primary: "#a855f7",
        light: "#f3e8ff",
        dark: "#7c3aed",
        badge: "#c084fc",
      };
    } else if (layerName.includes("RWN") || layerName.includes("Water")) {
      colorScheme = {
        primary: "#0ea5e9",
        light: "#e0f2fe",
        dark: "#0284c7",
        badge: "#38bdf8",
      };
    } else if (layerName.includes("RSW")) {
      colorScheme = {
        primary: "#64748b",
        light: "#f1f5f9",
        dark: "#475569",
        badge: "#94a3b8",
      };
    } else if (layerName.includes("RWWN")) {
      colorScheme = {
        primary: "#d97706",
        light: "#fef3c7",
        dark: "#b45309",
        badge: "#f59e0b",
      };
    }

    html += `
    <!-- Feature Card ${index + 1} -->
    <div style="
      margin-top: ${index > 0 ? "20px" : "0"};
      background: white;
      border: 1px solid #e2e8f0;
      border-radius: 16px;
      overflow: hidden;
      box-shadow: 0 1px 3px rgba(0,0,0,0.05);
      transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    ">
      <!-- Feature Header -->
      <div style="
        background: linear-gradient(135deg, ${
          colorScheme.light
        } 0%, #ffffff 100%);
        padding: 16px 20px;
        border-bottom: 1px solid #e2e8f0;
        display: flex;
        align-items: center;
        justify-content: space-between;
      ">
        <div style="display: flex; align-items: center; gap: 12px;">
          <div style="
            width: 12px;
            height: 12px;
            background: ${colorScheme.primary};
            border-radius: 50%;
            box-shadow: 0 0 0 4px ${colorScheme.light};
            flex-shrink: 0;
          "></div>
          <div>
            <div style="
              font-size: 14px;
              font-weight: 700;
              color: ${colorScheme.dark};
              letter-spacing: 0.02em;
              text-transform: uppercase;
              margin-bottom: 2px;
            ">${layerName}</div>
            <div style="
              font-size: 11px;
              color: #64748b;
              font-weight: 500;
              text-transform: none;
            ">Infrastructure Layer</div>
          </div>
        </div>
        <div style="
          background: ${colorScheme.light};
          color: ${colorScheme.dark};
          padding: 4px 10px;
          border-radius: 6px;
          font-size: 11px;
          font-weight: 600;
          border: 1px solid ${colorScheme.primary}20;
        ">ID ${index + 1}</div>
      </div>

      <!-- Drawing Link Section -->
      ${
        drawingInfo.value && viewerUrl
          ? `
      <div style="
        margin: 16px 20px;
        padding: 16px;
        background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
        border-radius: 12px;
        border: 1px solid #fde68a;
        position: relative;
        overflow: hidden;
      ">
        <div style="
          position: absolute;
          top: -20px;
          right: -20px;
          width: 80px;
          height: 80px;
          background: rgba(251, 191, 36, 0.1);
          border-radius: 50%;
        "></div>
        <div style="position: relative; z-index: 1;">
          <div style="
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 12px;
          ">
            <div style="
              width: 32px;
              height: 32px;
              background: #fbbf24;
              border-radius: 8px;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 16px;
            ">📄</div>
            <div>
              <div style="
                font-size: 11px;
                color: #92400e;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.05em;
                margin-bottom: 2px;
              ">${drawingInfo.label}</div>
              <div style="
                font-size: 13px;
                color: #78350f;
                font-weight: 700;
                font-family: 'Monaco', 'Menlo', monospace;
                background: rgba(255,255,255,0.8);
                padding: 4px 8px;
                border-radius: 6px;
                display: inline-block;
                border: 1px solid #fde68a;
              ">${drawingInfo.value}</div>
            </div>
          </div>
          <a href="${viewerUrl}" target="_blank" rel="noopener" style="
            display: inline-flex;
            align-items: center;
            gap: 8px;
            padding: 10px 18px;
            background: linear-gradient(135deg, #007cba 0%, #005a8b 100%);
            color: white;
            text-decoration: none;
            border-radius: 8px;
            font-size: 13px;
            font-weight: 600;
            box-shadow: 0 4px 12px rgba(0, 124, 186, 0.25);
            transition: all 0.2s;
            border: none;
          " onmouseover="this.style.transform='translateY(-1px)'; this.style.boxShadow='0 6px 16px rgba(0, 124, 186, 0.35)';" onmouseout="this.style.transform='translateY(0)'; this.style.boxShadow='0 4px 12px rgba(0, 124, 186, 0.25)';">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style="flex-shrink: 0;">
              <path d="M8 2L14 8L8 14M12 8H2" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            View Drawing
          </a>
        </div>
      </div>
      `
          : ""
      }

      <!-- Properties Table -->
      <div style="padding: 0 20px 20px;">
        <div style="
          font-size: 11px;
          font-weight: 700;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.1em;
          margin-bottom: 12px;
          padding-bottom: 8px;
          border-bottom: 2px solid #f1f5f9;
        ">Properties</div>
        <div style="
          display: grid;
          gap: 0;
          background: #f8fafc;
          border-radius: 10px;
          overflow: hidden;
          border: 1px solid #e2e8f0;
        ">`;

    // Filter out internal properties (starting with _) for cleaner display
    const entries = Object.entries(props).filter(([k]) => !k.startsWith("_"));

    // Show message if no displayable properties
    if (entries.length === 0) {
      html += `
          <div style="
            padding: 16px;
            text-align: center;
            color: #94a3b8;
            font-style: italic;
            font-size: 12px;
          ">No additional properties available</div>`;
    } else {
      entries.forEach(([k, v], idx) => {
        html += `
          <div style="
            display: grid;
            grid-template-columns: 1fr 2fr;
            gap: 16px;
            padding: 12px 16px;
            background: ${idx % 2 === 0 ? "#ffffff" : "#f8fafc"};
            border-bottom: ${
              idx < entries.length - 1 ? "1px solid #e2e8f0" : "none"
            };
            transition: background 0.2s;
          ">
            <div style="
              font-size: 12px;
              font-weight: 600;
              color: #475569;
              letter-spacing: 0.01em;
            ">${k}</div>
            <div style="
              font-size: 12px;
              color: #64748b;
              word-break: break-word;
              font-weight: 500;
            ">${
              String(v) ||
              '<span style="color: #94a3b8; font-style: italic;">N/A</span>'
            }</div>
          </div>`;
      });
    }

    html += `
        </div>
      </div>
    </div>`;
  });

  html += `
    </div>
  </div>`;

  return html;
}

// Component to keep popup open during zoom/pan
function PopupPersist({ popup, setPopup }) {
  const map = useMap();

  useEffect(() => {
    if (!popup) return;

    const originalLatLng = popup.latlng;

    const updatePopupPosition = () => {
      if (!popup || !map) return;

      const popupElement = map
        .getContainer()
        .querySelector(".feature-info-popup");
      if (popupElement) {
        const popupPane = map.getPane("popupPane");
        if (popupPane) {
          popupElement.style.visibility = "visible";
          popupElement.style.opacity = "1";
        }
      }
    };

    map.on("zoom", updatePopupPosition);
    map.on("move", updatePopupPosition);
    map.on("zoomend", updatePopupPosition);
    map.on("moveend", updatePopupPosition);

    return () => {
      map.off("zoom", updatePopupPosition);
      map.off("move", updatePopupPosition);
      map.off("zoomend", updatePopupPosition);
      map.off("moveend", updatePopupPosition);
    };
  }, [map, popup, setPopup]);

  return null;
}

// Main FeatureInfoPopup component
export default function FeatureInfoPopup({
  popup,
  setPopup,
  generateViewerUrl,
  getDrawingIdInfo,
}) {
  const map = useMap();

  const htmlContent = popup?.features
    ? generateFeatureInfoHTML(
        popup.features,
        generateViewerUrl,
        getDrawingIdInfo
      )
    : popup?.content || "";

  if (!popup) return null;

  return (
    <>
      <PopupPersist popup={popup} setPopup={setPopup} />
      <Popup
        position={popup.latlng}
        closeOnClick={false}
        autoClose={false}
        closeButton={true}
        className="feature-info-popup"
        eventHandlers={{
          remove: () => setPopup(null),
        }}
      >
        <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
      </Popup>
      <style>{`
        .feature-info-popup .leaflet-popup-content-wrapper {
          border-radius: 16px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05);
          padding: 0;
          overflow: hidden;
          position: relative;
          background: white;
        }
        .feature-info-popup .leaflet-popup-content {
          margin: 0;
          padding: 0;
          min-width: 480px;
          max-width: 720px;
          width: auto;
        }
        .feature-info-popup .leaflet-popup-tip {
          background: white;
          box-shadow: 0 4px 12px rgba(0,0,0,0.1);
          border: 1px solid rgba(0,0,0,0.05);
        }
        .feature-info-popup .leaflet-popup-close-button {
          color: white !important;
          font-size: 20px !important;
          font-weight: 600 !important;
          padding: 0 !important;
          z-index: 1000 !important;
          position: absolute !important;
          top: 20px !important;
          right: 30px !important;
          width: 36px !important;
          height: 36px !important;
          line-height: 36px !important;
          text-align: center !important;
          background: rgba(0, 0, 0, 0.5) !important;
          backdrop-filter: blur(8px) !important;
          border-radius: 10px !important;
          text-decoration: none !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1) !important;
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
        }
        .feature-info-popup .leaflet-popup-close-button:hover {
          color: white !important;
          background: rgba(0, 0, 0, 0.7) !important;
          transform: scale(1.05) !important;
          border-color: rgba(255, 255, 255, 0.3) !important;
        }
        .feature-info-popup .leaflet-popup-content-wrapper::-webkit-scrollbar {
          width: 8px;
        }
        .feature-info-popup .leaflet-popup-content-wrapper::-webkit-scrollbar-track {
          background: #f1f5f9;
        }
        .feature-info-popup .leaflet-popup-content-wrapper::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 4px;
        }
        .feature-info-popup .leaflet-popup-content-wrapper::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </>
  );
}
